var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import Header from '../../ui/atoms/Header';
import { Inset, Stack } from 'syf-component-library/ui/spacing';
import Radio from '../../ui/atoms/RadioButton/subcomponents';
import { SlideInModalPortal } from 'syf-component-library/ui/patterns/SlideInModalPortal';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { ButtonType, ModalType } from '../../redux/ui/typings';
import { closeModal } from '../../redux/ui/ui.slice';
import MessageBox from 'syf-component-library/ui/patterns/MessageBox';
import Icon from '../../ui/atoms/Icon';
import TextArea from 'syf-component-library/ui/atoms/Textarea';
import { Body } from 'syf-component-library/ui/typography';
import { triggerAnalyticsEvent } from 'syf-js-utilities';
import { Analytics } from '../../helpers/analyticsMiddleware';
import { clearCancelDisputeState } from '../../redux/cancelDispute/cancelDispute.slice';
import SYFLoader from '../../ui/atoms/Loader';
import { t } from '../../strings/i18n/i18n';
import ModalHeaderStrip from '../../ui/atoms/ModalHeaderStrip';
import useDDPreventScroll from '../../hooks/useDDPreventScrolling';
import { CustomModalFooter } from '../../ui/patterns/SlideinModal/subcomponents';
import DisputeModalHeader from '../../ui/organisms/DisputeModalHeader';
import { CANCEL_DISPUTE_REASON_CHARACTER_LIMIT } from '../../const/characterValidation';
import useTextareaSizing from '../../hooks/useTextareaSizing';
import DisputesImage from '../../ui/atoms/DisputesImage';
import SLIDE_IN_MODAL_CONTAINER_ID from '../../const/modalContainerIds';
import useSelectedDispute from '../../hooks/useSelectedDispute';
import useWindowSize from 'syf-component-library/ui/customHooks/useWindowWidth';
import { CancelFieldSet, CancelWrapper, Description, DisputeImageContainer, HeaderWrapper, SubtitleText, TextAreaWrapper } from './subcomponents/subcomponents';
import CancelDisputeStatus from './subcomponents/CancelDisputeStatus';
import { handleTextChange, primaryButtonHandler } from './handlers';
import { checkForAnalyticsEvent, getAnalyticsPageName, getCancelStatusType, getHelpText, getPrimaryButtonReason, getPrimaryButtonText, setSecondaryButtonHandler } from './helpers';
var CANCEL_REASONS_KEY = [
    'reason1',
    'reason2',
    'reason3',
    'reason4',
    'reason5'
];
var CancelDispute = function () {
    var maxHeight = useTextareaSizing({
        maximumCharacters: CANCEL_DISPUTE_REASON_CHARACTER_LIMIT
    }).maxHeight;
    var dispatch = useDispatch();
    var _a = useSelector(function (state) { return state; }, shallowEqual), _b = _a.ui, formError = _b.formError, modalType = _b.modalType, isLoading = _b.isLoading, _c = _a.cancelDispute, isCanceled = _c.isCanceled, cancelDisputeError = _c.cancelDisputeError, _d = _a.appConfig, accountId = _d.accountId, _e = _d.selectedDispute, caseId = _e.caseId, transactionId = _e.transactionId, assetsURL = _a.mfeConfig.assetsURL;
    var isLessThanTablet = useWindowSize().isLessThanTablet;
    var imageSize = isLessThanTablet ? 'xsmall' : 'small';
    var selectedTransaction = useSelectedDispute().selectedTransaction;
    var _f = useState(''), selectedOption = _f[0], setSelectedOption = _f[1];
    var _g = useState(''), otherReason = _g[0], setOtherReason = _g[1];
    var _h = useState(''), otherReasonError = _h[0], setOtherReasonError = _h[1];
    var _j = useState(false), showError = _j[0], setShowError = _j[1];
    var disputeTitle = selectedTransaction === null || selectedTransaction === void 0 ? void 0 : selectedTransaction.merchantName;
    var disputeStatus = selectedTransaction === null || selectedTransaction === void 0 ? void 0 : selectedTransaction.statusCode;
    var selectReasonRef = useRef(null);
    var isCancelSubmitted = isCanceled || !!cancelDisputeError;
    var primaryButtonText = getPrimaryButtonText(isCancelSubmitted);
    var primaryButtonReason = getPrimaryButtonReason(isCancelSubmitted);
    var maxCharactersReached = (otherReason === null || otherReason === void 0 ? void 0 : otherReason.length) >= CANCEL_DISPUTE_REASON_CHARACTER_LIMIT;
    var keyChar;
    var charactersRemaining = CANCEL_DISPUTE_REASON_CHARACTER_LIMIT - otherReason.length;
    var helpText = getHelpText(charactersRemaining);
    var handleChange = function (option) {
        setShowError(false);
        setOtherReason('');
        setOtherReasonError('');
        setSelectedOption(option);
    };
    var fireCloseModal = function () {
        dispatch(closeModal());
    };
    var handleKeyDown = function (e) {
        keyChar = e.key;
        return maxCharactersReached
            ? setOtherReasonError("".concat(t('formError.moreThanHundredCharacters')))
            : null;
    };
    useDDPreventScroll();
    useEffect(function () {
        return function () {
            dispatch(clearCancelDisputeState());
        };
    }, [dispatch]);
    useEffect(function () {
        var analyticsPageName = getAnalyticsPageName(isCancelSubmitted, cancelDisputeError);
        triggerAnalyticsEvent({
            PageKind: Analytics.Dispute,
            PageFunction: Analytics.Billing,
            PageSubFunction: Analytics.Documents,
            pageName: analyticsPageName
        });
    }, [cancelDisputeError, isCancelSubmitted]);
    useEffect(function () {
        checkForAnalyticsEvent(formError);
    }, [formError]);
    useEffect(function () {
        if (showError) {
            selectReasonRef.current.focus();
        }
    }, [showError]);
    var mainContent = isCancelSubmitted ? (_jsx(CancelDisputeStatus, { disputeTitle: disputeTitle, type: getCancelStatusType(isCanceled), "data-cy": "cancel-dispute-modal" })) : (_jsxs(_Fragment, { children: [_jsx(ModalHeaderStrip, { title: disputeTitle, status: disputeStatus }), _jsxs(CancelWrapper, __assign({ "data-cy": "cancel-dispute-modal" }, { children: [_jsx(DisputeImageContainer, { children: _jsx(DisputesImage, { assetsHost: assetsURL, imageName: "CANCEL", size: imageSize }) }), _jsx(HeaderWrapper, { children: _jsx(Header, { content: "".concat(t('cancelDispute.modalHeader')), size: "medium" }) }), _jsx(Description, { children: t('cancelDispute.modalDescription') }), showError && (_jsx(Inset, __assign({ top: "large" }, { children: _jsx(MessageBox, __assign({ type: "error", icon: _jsx(Icon, { name: "faExclamationCircle", size: "lg" }), ref: selectReasonRef, tabIndex: -1, "data-testid": "cancel-error-div" }, { children: t('formError.pleaseSelect') })) }))), _jsxs(CancelFieldSet, __assign({ role: "radiogroup", "aria-invalid": showError }, { children: [_jsx(SubtitleText, { children: t('cancelDispute.modalSubtitle') }), CANCEL_REASONS_KEY.map(function (key) { return (_jsx(Radio, __assign({ "aria-checked": selectedOption === key, hasError: false, id: key, name: "cancel-reason-radio", checked: selectedOption === key, value: key, onChange: function () {
                                    handleChange(key);
                                }, dataCy: "".concat(key) }, { children: t("cancelDispute.".concat(key)) }), "".concat(key, "-radio"))); })] })), selectedOption === 'reason5' && (_jsxs(TextAreaWrapper, { children: [_jsx(Body, { children: t('cancelDispute.securityDisclaimerHelpText') }), _jsx(Body, { children: t('cancelDispute.otherReason') }), _jsx(Stack, { size: "small" }), _jsx(TextArea, { "data-testid": "other-reason-text-area", dynamicHeight: true, dynamicHeightMax: maxHeight, error: otherReasonError, errorIcon: _jsx(Icon, { name: "falExclamationCircle", size: "sm" }), helpText: helpText, id: "syf-text-area", maxLength: 100, onChange: function (e) {
                                    return handleTextChange({
                                        e: e,
                                        setOtherReason: setOtherReason,
                                        setOtherReasonError: setOtherReasonError,
                                        keyChar: keyChar,
                                        maxCharactersReached: maxCharactersReached
                                    });
                                }, onKeyDown: handleKeyDown, placeholder: "", sizeRatio: 1, value: otherReason, width: "758px" }), _jsx(Stack, {})] }))] }))] }));
    var renderContent = function () {
        return isLoading && modalType === ModalType.CancelDispute ? (_jsx(SYFLoader, {})) : (mainContent);
    };
    return (_jsx("aside", { children: _jsx(SlideInModalPortal, __assign({ "aria-label": "".concat(t('label.cancelDisputeModal')), id: "modal", containerId: SLIDE_IN_MODAL_CONTAINER_ID, isOpen: true, escapeModalHandler: fireCloseModal, header: _jsx(DisputeModalHeader, { dataType: "dispute-transaction", dataTitle: "dispute-transaction", dataReason: "close", content: "".concat(t('cancelDispute.title')), closeModal: fireCloseModal }), footer: _jsx(CustomModalFooter, { primaryText: primaryButtonText, handlePrimaryButton: function () {
                    return primaryButtonHandler({
                        fireCloseModal: fireCloseModal,
                        dispatch: dispatch,
                        isCancelSubmitted: isCancelSubmitted,
                        selectedOption: selectedOption,
                        setShowError: setShowError,
                        setOtherReasonError: setOtherReasonError,
                        otherReason: otherReason,
                        caseId: caseId,
                        transactionId: transactionId,
                        accountId: accountId
                    });
                }, dataType: "dispute-transaction", dataPrimaryTitle: "dispute-transaction", dataSecondaryTitle: "dispute-transaction", dataPrimaryReason: primaryButtonReason, secondaryText: "".concat(t('button.neverMind')), handleSecondaryButton: setSecondaryButtonHandler(isCancelSubmitted, fireCloseModal), secondaryButtonType: ButtonType.Ghost, dataSecondaryReason: "Never mind", dataPrimaryObject: "button", dataSecondaryObject: "button" }) }, { children: renderContent() })) }));
};
export default CancelDispute;
//# sourceMappingURL=index.js.map